import { HttpErrorResponse } from '@angular/common/http';

/**
 * Lifecycle of a tenant as published by the master tenant record
 * (GET {master}/api/v1/tenants/{subdomain}). Older masters do not send the
 * field at all, which means the tenant is a regular, paying one.
 */
export type TenantLifecycleState = 'Active' | 'Trial' | 'TrialExpired' | 'ListeningDisabled' | 'Deleting';

export interface TenantLifecycleFields {
  lifecycleState?: string | null;
  trialEndsAt?: string | null;
  listeningEndsAt?: string | null;
  deletionAt?: string | null;
  /** The plan is already activated during the trial; Stripe starts charging at trialEndsAt. */
  activationScheduled?: boolean | null;
}

/** Which banner the admin shell shows for a lifecycle state. */
export type TenantLifecycleBannerKind = 'none' | 'trial' | 'read-only' | 'offline';

/** 403 body reasons the tenant API uses for lifecycle rejections. */
export const TENANT_READ_ONLY_REASON = 'tenant_read_only';
export const TENANT_LISTENING_DISABLED_REASON = 'tenant_listening_disabled';
export type TenantLifecycleRejectionReason = typeof TENANT_READ_ONLY_REASON | typeof TENANT_LISTENING_DISABLED_REASON;

const KNOWN_STATES: readonly TenantLifecycleState[] = [
  'Active',
  'Trial',
  'TrialExpired',
  'ListeningDisabled',
  'Deleting',
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Missing, empty or unknown values fall back to `Active` so older masters keep working. */
export function normalizeLifecycleState(raw: string | null | undefined): TenantLifecycleState {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return 'Active';
  }

  const wanted = raw.trim().toLowerCase();
  return KNOWN_STATES.find((state) => state.toLowerCase() === wanted) ?? 'Active';
}

/** From the end of the trial on, the workspace only accepts reads. */
export function isReadOnlyLifecycleState(state: TenantLifecycleState): boolean {
  return state === 'TrialExpired' || state === 'ListeningDisabled' || state === 'Deleting';
}

/** From `ListeningDisabled` on, listeners can no longer play anything. */
export function isListeningDisabledLifecycleState(state: TenantLifecycleState): boolean {
  return state === 'ListeningDisabled' || state === 'Deleting';
}

export function lifecycleBannerKind(state: TenantLifecycleState): TenantLifecycleBannerKind {
  switch (state) {
    case 'Trial':
      return 'trial';
    case 'TrialExpired':
      return 'read-only';
    case 'ListeningDisabled':
    case 'Deleting':
      return 'offline';
    default:
      return 'none';
  }
}

/** Parses an ISO instant; returns null for missing or unparsable values. */
export function parseLifecycleInstant(value: string | null | undefined): Date | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whole days left in the trial, rounded up so "ends tomorrow evening" still
 * reads as one day. Never negative; null when the end date is unknown.
 */
export function trialDaysRemaining(trialEndsAt: string | null | undefined, now: Date = new Date()): number | null {
  const end = parseLifecycleInstant(trialEndsAt);
  if (end === null) {
    return null;
  }

  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / DAY_MS));
}

/**
 * Extracts the lifecycle reason from a 403 response of the tenant API or CDN.
 * The body is usually parsed JSON, but text responses carry it as a string.
 */
export function tenantLifecycleRejectionReason(error: unknown): TenantLifecycleRejectionReason | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
    return null;
  }

  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return null;
    }
  }

  if (body === null || typeof body !== 'object') {
    return null;
  }

  const reason = (body as { reason?: unknown }).reason;
  return reason === TENANT_READ_ONLY_REASON || reason === TENANT_LISTENING_DISABLED_REASON ? reason : null;
}
